// Section permissions (client #95).
//
// The client asked for Lesen / Erstellen / Bearbeiten / Löschen per app section,
// set per role (Techniker, Büro) under Verwaltung → Berechtigungen. Admins
// always have every right and are not part of the matrix.
//
// Two layers decide what a user may do:
//
//   1. The server floor. The CouchDB validator (infra/couchdb/design/access.js,
//      mirrored by `canWrite` in lib/permissions.ts) decides which doc types a
//      role may write. It does not read settings, so nothing configured here can
//      ever exceed it: a right the validator would reject is capped to false and
//      shown greyed out ("Nur Admin") in the matrix.
//   2. The matrix in `settings.permissions`, which can only tighten the floor.
//
// The defaults reproduce the behaviour before #95 exactly, so deploying this
// changes nothing until an admin edits the matrix.
//
// Everything in this file is pure; components read it through
// lib/use-section-rights.ts and the store enforces it in guardWrite.

import type {
  Customer,
  HoseConfig,
  SectionPermissions,
  SectionRight,
  Settings,
  StoredSectionRights,
} from '@/data/types';
import { canWrite, EDITABLE_TYPES } from '@/lib/permissions';

export type { SectionRight } from '@/data/types';

export enum SectionId {
  CUSTOMERS = 'customers',
  EXTINGUISHERS = 'extinguishers',
  HOSES = 'hoses',
  MAINTENANCE = 'maintenance',
  CALENDAR = 'calendar',
  MAP = 'map',
  REMINDERS = 'reminders',
  REPORTS = 'reports',
  INVOICES = 'invoices',
  MANUFACTURERS = 'manufacturers',
  DEVICE_TYPES = 'deviceTypes',
  SPARE_PARTS = 'spareParts',
  SERVICE_PRICES = 'servicePrices',
  HOSE_OPTIONS = 'hoseOptions',
  WORKERS = 'workers',
  SETTINGS = 'settings',
}

export enum PermAction {
  READ = 'read',
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
}

/** The roles an org_admin configures in the matrix. */
export enum MatrixRole {
  TECHNICIAN = 'technician',
  STAFF = 'staff',
}

export type SectionRights = Record<SectionId, SectionRight>;

/** One right the store checks before a write: [section, action]. */
export type SectionCheck = readonly [SectionId, PermAction];

export const ACTIONS: readonly PermAction[] = [
  PermAction.READ,
  PermAction.CREATE,
  PermAction.EDIT,
  PermAction.DELETE,
];

const WRITE_ACTIONS = [
  PermAction.CREATE,
  PermAction.EDIT,
  PermAction.DELETE,
] as const;
type WriteAction = (typeof WRITE_ACTIONS)[number];

/** Day-to-day work, in the order the matrix lists it. */
export const WORK_SECTIONS: readonly SectionId[] = [
  SectionId.CUSTOMERS,
  SectionId.EXTINGUISHERS,
  SectionId.HOSES,
  SectionId.MAINTENANCE,
  SectionId.CALENDAR,
  SectionId.MAP,
  SectionId.REMINDERS,
  SectionId.REPORTS,
  SectionId.INVOICES,
];

/** The Verwaltung pages, in sidebar order. */
export const ADMIN_SECTIONS: readonly SectionId[] = [
  SectionId.MANUFACTURERS,
  SectionId.DEVICE_TYPES,
  SectionId.SPARE_PARTS,
  SectionId.SERVICE_PRICES,
  SectionId.HOSE_OPTIONS,
  SectionId.WORKERS,
  SectionId.SETTINGS,
];

export const ALL_SECTIONS: readonly SectionId[] = [
  ...WORK_SECTIONS,
  ...ADMIN_SECTIONS,
];

// What each write right persists, as CouchDB doc types. This drives the server
// floor (every listed type must pass canWrite) and the legacy permMatrix
// conversion. A section without an entry for an action has no such action: the
// matrix shows "–" there and the right is always false.
//
// Meaning of the rights where the section name alone does not say:
//   - customers: edit = the customer record itself (name, addresses, contacts,
//     notes, attachments, service contract, linked customers, archive
//     snapshots). Creating a customer includes the devices copied by
//     "Duplicate"; deleting one takes its embedded devices with it.
//   - extinguishers / hoses: the devices are embedded in the customer doc, so
//     they persist as `customer` writes. Create = add / copy (replace adds the
//     new unit and edits the old one, so it needs both); edit = change a device,
//     reorder the list, section dividers; delete = remove a device.
//   - maintenance: create = carry out a maintenance — start it (walk-in or from
//     a calendar visit) and complete it, which writes the visit, the inspection
//     record and the devices' service dates. Edit = change the maintenance state
//     of devices without a new inspection record ("maintained this cycle" tick,
//     "inspect all"). There is no delete: inspection records are the compliance
//     trail and the app offers no way to remove one.
//   - calendar: create / edit / delete visits (date, time, technician, notes).
//   - invoices: an invoice is generated from the inspections and never stored,
//     so there is nothing for the server to reject (no floor). Create = print it
//     or export the PDF; edit = adjust line prices and VAT on screen before
//     printing. No delete — there is no stored invoice to delete.
//   - map, reminders, reports: read-only views. Generating a report is part of
//     reading it.
//   - manufacturers include their models; spare-part edit includes renaming an
//     article number, which repoints devices and inspections; hose options are
//     added or removed (there is no rename); settings is a single record, so it
//     can only be read or edited.
const WRITE_TYPES: Record<
  SectionId,
  Partial<Record<WriteAction, readonly string[]>>
> = {
  [SectionId.CUSTOMERS]: {
    [PermAction.CREATE]: ['customer'],
    [PermAction.EDIT]: ['customer', 'archive'],
    [PermAction.DELETE]: ['customer'],
  },
  [SectionId.EXTINGUISHERS]: {
    [PermAction.CREATE]: ['customer', 'extinguisher'],
    [PermAction.EDIT]: ['customer', 'extinguisher'],
    [PermAction.DELETE]: ['customer', 'extinguisher'],
  },
  [SectionId.HOSES]: {
    [PermAction.CREATE]: ['customer', 'hose'],
    [PermAction.EDIT]: ['customer', 'hose'],
    [PermAction.DELETE]: ['customer', 'hose'],
  },
  [SectionId.MAINTENANCE]: {
    [PermAction.CREATE]: ['visit', 'inspection', 'customer'],
    [PermAction.EDIT]: ['customer'],
  },
  [SectionId.CALENDAR]: {
    [PermAction.CREATE]: ['visit'],
    [PermAction.EDIT]: ['visit'],
    [PermAction.DELETE]: ['visit'],
  },
  [SectionId.MAP]: {},
  [SectionId.REMINDERS]: {},
  [SectionId.REPORTS]: {},
  [SectionId.INVOICES]: {
    [PermAction.CREATE]: [],
    [PermAction.EDIT]: [],
  },
  [SectionId.MANUFACTURERS]: {
    [PermAction.CREATE]: ['manufacturer'],
    [PermAction.EDIT]: ['manufacturer'],
    [PermAction.DELETE]: ['manufacturer'],
  },
  [SectionId.DEVICE_TYPES]: {
    [PermAction.CREATE]: ['device_type'],
    [PermAction.EDIT]: ['device_type'],
    [PermAction.DELETE]: ['device_type'],
  },
  [SectionId.SPARE_PARTS]: {
    [PermAction.CREATE]: ['spare_part'],
    [PermAction.EDIT]: ['spare_part', 'customer', 'inspection'],
    [PermAction.DELETE]: ['spare_part'],
  },
  [SectionId.SERVICE_PRICES]: {
    [PermAction.CREATE]: ['service_price'],
    [PermAction.EDIT]: ['service_price'],
    [PermAction.DELETE]: ['service_price'],
  },
  [SectionId.HOSE_OPTIONS]: {
    [PermAction.CREATE]: ['hose_config'],
    [PermAction.DELETE]: ['hose_config'],
  },
  [SectionId.WORKERS]: {
    [PermAction.CREATE]: ['worker'],
    [PermAction.EDIT]: ['worker'],
    [PermAction.DELETE]: ['worker'],
  },
  [SectionId.SETTINGS]: {
    [PermAction.EDIT]: ['settings'],
  },
};

// What each role could open before #95: /reports was gated to org_admin +
// staff, every /admin/* page to org_admin. Everything else was open to all.
const DEFAULT_READ: Record<MatrixRole, readonly SectionId[]> = {
  [MatrixRole.TECHNICIAN]: [
    SectionId.CUSTOMERS,
    SectionId.EXTINGUISHERS,
    SectionId.HOSES,
    SectionId.MAINTENANCE,
    SectionId.CALENDAR,
    SectionId.MAP,
    SectionId.REMINDERS,
    SectionId.INVOICES,
  ],
  [MatrixRole.STAFF]: [
    SectionId.CUSTOMERS,
    SectionId.EXTINGUISHERS,
    SectionId.HOSES,
    SectionId.MAINTENANCE,
    SectionId.CALENDAR,
    SectionId.MAP,
    SectionId.REMINDERS,
    SectionId.REPORTS,
    SectionId.INVOICES,
  ],
};

const NONE: SectionRight = {
  read: false,
  create: false,
  edit: false,
  delete: false,
};

/** Whether `action` exists for `section` at all (false renders as "–"). */
export function isApplicable(section: SectionId, action: PermAction): boolean {
  if (action === PermAction.READ) return true;
  return WRITE_TYPES[section][action as WriteAction] !== undefined;
}

/** The actions a section has, in column order. */
export function applicableActions(section: SectionId): PermAction[] {
  return ACTIONS.filter(a => isApplicable(section, a));
}

/** `_admin` and org_admin always have every right; never configurable. */
export function hasFullAccess(roles: readonly string[]): boolean {
  return roles.includes('_admin') || roles.includes('org_admin');
}

/**
 * Which matrix row applies to a user. Staff wins over technician, matching the
 * validator. null for full-access users and for users with neither role
 * (super_admin without impersonation, or no recognised role).
 */
export function matrixRoleOf(roles: readonly string[]): MatrixRole | null {
  if (hasFullAccess(roles)) return null;
  if (roles.includes('staff')) return MatrixRole.STAFF;
  if (roles.includes('technician')) return MatrixRole.TECHNICIAN;
  return null;
}

function mapSections(fn: (s: SectionId) => SectionRight): SectionRights {
  const out = {} as SectionRights;
  for (const s of ALL_SECTIONS) out[s] = fn(s);
  return out;
}

export function allRights(): SectionRights {
  return mapSections(s => ({
    read: true,
    create: isApplicable(s, PermAction.CREATE),
    edit: isApplicable(s, PermAction.EDIT),
    delete: isApplicable(s, PermAction.DELETE),
  }));
}

/** May these roles make this write, as far as the server is concerned? */
function floorAllows(
  roles: readonly string[],
  section: SectionId,
  action: WriteAction
): boolean {
  const types = WRITE_TYPES[section][action];
  if (!types) return false;
  return types.every(type => canWrite([...roles], type));
}

/**
 * Apply the rules no configuration can bypass: N/A actions are false, a write
 * the server would reject is false, and without Lesen nothing else is allowed.
 */
export function capRights(
  rights: SectionRights,
  roles: readonly string[]
): SectionRights {
  return mapSections(s => {
    const r = rights[s] ?? NONE;
    if (!r.read) return { ...NONE };
    return {
      read: true,
      create: r.create && floorAllows(roles, s, PermAction.CREATE),
      edit: r.edit && floorAllows(roles, s, PermAction.EDIT),
      delete: r.delete && floorAllows(roles, s, PermAction.DELETE),
    };
  });
}

/**
 * The most a role can be granted: everything readable, and every write the
 * validator accepts for that role. Cells above this are greyed out.
 */
export function maxRights(role: MatrixRole): SectionRights {
  return capRights(allRights(), [role]);
}

/** Today's behaviour for a role (= "Standard wiederherstellen"). */
export function defaultRights(role: MatrixRole): SectionRights {
  const reads = DEFAULT_READ[role];
  return capRights(
    mapSections(s =>
      reads.includes(s)
        ? { read: true, create: true, edit: true, delete: true }
        : { ...NONE }
    ),
    [role]
  );
}

/**
 * Convert the pre-#95 write allow-list (settings.permMatrix[role], a list of
 * doc types). A doc type the old matrix let an admin switch off, and that is
 * missing from the list, turns off every write right that persists it. Types
 * the old page never offered (signature, photo_ref, …) are ignored — they were
 * not a choice anyone made. The old matrix had no read dimension and could not
 * loosen the server, so reads stay at the defaults and nothing is turned on.
 */
export function fromLegacyMatrix(
  role: MatrixRole,
  allowed: readonly string[]
): SectionRights {
  const editable = EDITABLE_TYPES as readonly string[];
  const base = defaultRights(role);
  return mapSections(s => {
    const r = { ...base[s] };
    for (const a of WRITE_ACTIONS) {
      const types = WRITE_TYPES[s][a] ?? [];
      const blocked = types.some(
        type => editable.includes(type) && !allowed.includes(type)
      );
      if (blocked) r[a] = false;
    }
    return r;
  });
}

/** Read a stored (possibly partial or older) matrix row, filling gaps. */
function fromStored(
  role: MatrixRole,
  stored: StoredSectionRights
): SectionRights {
  const fallback = defaultRights(role);
  return mapSections(s => {
    const r = stored[s];
    if (!r) return fallback[s];
    return {
      read: r.read === true,
      create: r.create === true,
      edit: r.edit === true,
      delete: r.delete === true,
    };
  });
}

type PermSettings = Pick<Settings, 'permissions' | 'permMatrix'>;

/**
 * The matrix row as configured for a role, before the floor: the new
 * `settings.permissions` when present, else the converted legacy permMatrix,
 * else the defaults. Capped, so the matrix editor never shows an impossible
 * state.
 */
export function configuredRights(
  role: MatrixRole,
  settings?: Partial<PermSettings>
): SectionRights {
  const stored = settings?.permissions?.[role];
  if (stored) return capRights(fromStored(role, stored), [role]);
  const legacy = settings?.permMatrix?.[role];
  if (legacy) return fromLegacyMatrix(role, legacy);
  return defaultRights(role);
}

/** The effective rights of a signed-in user. */
export function rightsFor(
  roles: readonly string[],
  settings?: Partial<PermSettings>
): SectionRights {
  if (hasFullAccess(roles)) return allRights();
  const role = matrixRoleOf(roles);
  if (role) return capRights(configuredRights(role, settings), roles);
  // super_admin without impersonation, or no recognised role: keep the rules
  // from before #95 exactly — the pages that were not role-gated, and writes
  // decided by the validator alone (nothing for super_admin; everything for a
  // user without a role, matching the server's fall-through). The matrix does
  // not apply to them, so neither does the Lesen coupling.
  const reads = DEFAULT_READ[MatrixRole.TECHNICIAN];
  return mapSections(s => ({
    read: reads.includes(s),
    create: floorAllows(roles, s, PermAction.CREATE),
    edit: floorAllows(roles, s, PermAction.EDIT),
    delete: floorAllows(roles, s, PermAction.DELETE),
  }));
}

export function can(
  roles: readonly string[],
  settings: Partial<PermSettings> | undefined,
  section: SectionId,
  action: PermAction
): boolean {
  return rightsFor(roles, settings)[section][action];
}

// ── Matrix editing (Permissions page) ─────────────────────────────────────

/**
 * Toggle one cell. Lesen gates the row: switching it off clears every other
 * right, and switching any write right on switches Lesen on. Rights above the
 * role's maximum never turn on.
 */
export function setRight(
  right: SectionRight,
  action: PermAction,
  value: boolean,
  max: SectionRight
): SectionRight {
  if (action === PermAction.READ) {
    return value ? { ...right, read: max.read } : { ...NONE };
  }
  if (!value) return { ...right, [action]: false };
  if (!max[action]) return right;
  return { ...right, read: true, [action]: true };
}

/** The "Alle" shortcut: everything the role may have, or nothing. */
export function setAll(value: boolean, max: SectionRight): SectionRight {
  return value ? { ...max } : { ...NONE };
}

/** Whether a row already has everything the role may have. */
export function hasAll(right: SectionRight, max: SectionRight): boolean {
  return ACTIONS.every(a => !max[a] || right[a]);
}

/** The stored form of a matrix (what the Permissions page saves). */
export function toStored(
  rights: Record<MatrixRole, SectionRights>
): SectionPermissions {
  return {
    technician: { ...rights[MatrixRole.TECHNICIAN] },
    staff: { ...rights[MatrixRole.STAFF] },
  };
}

// ── Navigation ────────────────────────────────────────────────────────────

/** Where each section lives (sections with a page of their own). */
export const SECTION_PATHS: Partial<Record<SectionId, string>> = {
  [SectionId.CUSTOMERS]: '/overview',
  [SectionId.CALENDAR]: '/calendar',
  [SectionId.MAP]: '/map',
  [SectionId.REMINDERS]: '/reminders',
  [SectionId.REPORTS]: '/reports',
  [SectionId.MANUFACTURERS]: '/admin/manufacturers',
  [SectionId.DEVICE_TYPES]: '/admin/device-types',
  [SectionId.SPARE_PARTS]: '/admin/spare-parts',
  [SectionId.SERVICE_PRICES]: '/admin/service-prices',
  [SectionId.HOSE_OPTIONS]: '/admin/hose-options',
  [SectionId.WORKERS]: '/admin/workers',
  [SectionId.SETTINGS]: '/admin/settings',
};

/**
 * Landing page for a user: the customer overview when they may read customers,
 * else the first page they may read. /overview is the last resort because it
 * is never gated — it renders a "no access" notice instead.
 */
export function firstReadablePath(rights: SectionRights): string {
  for (const s of ALL_SECTIONS) {
    const path = SECTION_PATHS[s];
    if (path && rights[s].read) return path;
  }
  return '/overview';
}

// ── Write classification (store guard) ────────────────────────────────────

export interface EmbeddedDiff {
  created: boolean;
  edited: boolean;
  deleted: boolean;
}

/**
 * Structural equality that ignores key order and treats a missing key and an
 * `undefined` value alike — device records are rebuilt field by field on save,
 * so reference or JSON comparison would see edits that did not happen.
 */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((x, i) => sameValue(x, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const ak = Object.keys(ao).filter(k => ao[k] !== undefined);
    const bk = Object.keys(bo).filter(k => bo[k] !== undefined);
    if (ak.length !== bk.length) return false;
    return ak.every(k => sameValue(ao[k], bo[k]));
  }
  return false;
}

/**
 * Compare two versions of an embedded device list by id: ids only in `next`
 * were created, ids only in `prev` were deleted, and a kept device was edited
 * when its content changed or the kept devices changed order (drag-and-drop).
 */
export function diffEmbedded<T extends { id: string }>(
  prev: readonly T[] = [],
  next: readonly T[] = []
): EmbeddedDiff {
  const before = new Map(prev.map(x => [x.id, x]));
  const afterIds = new Set(next.map(x => x.id));
  const kept = next.filter(x => before.has(x.id));
  const keptBefore = prev.filter(x => afterIds.has(x.id));
  return {
    created: next.some(x => !before.has(x.id)),
    deleted: prev.some(x => !afterIds.has(x.id)),
    edited:
      kept.some((x, i) => x.id !== keptBefore[i]?.id) ||
      kept.some(x => !sameValue(before.get(x.id), x)),
  };
}

// Customer fields that are not the customer record itself: the embedded device
// lists, the section-divider colours (part of the extinguisher list, #30), and
// the cached counts / status every write re-derives.
const NON_RECORD_FIELDS = new Set([
  'extinguishers',
  'hoses',
  'sectionColors',
  'deviceCounts',
  'status',
]);

function recordChanged(prev: Customer, next: Customer): boolean {
  const p = prev as unknown as Record<string, unknown>;
  const n = next as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(p), ...Object.keys(n)]);
  for (const k of keys) {
    if (NON_RECORD_FIELDS.has(k)) continue;
    if (!sameValue(p[k], n[k])) return true;
  }
  return false;
}

function pushDiff(out: SectionCheck[], section: SectionId, d: EmbeddedDiff) {
  if (d.created) out.push([section, PermAction.CREATE]);
  if (d.edited) out.push([section, PermAction.EDIT]);
  if (d.deleted) out.push([section, PermAction.DELETE]);
}

/**
 * The rights a customer-doc write needs. Creating or deleting a customer is
 * judged as a whole (customers.create / customers.delete). An update is split
 * into what actually changed: devices added / changed / removed check the
 * extinguisher or hose rights, anything else on the record checks
 * customers.edit. A write that changes nothing needs no section right.
 */
export function customerWriteChecks(
  prev: Customer | undefined,
  next: Customer | undefined
): SectionCheck[] {
  if (!prev && !next) return [];
  if (!prev) return [[SectionId.CUSTOMERS, PermAction.CREATE]];
  if (!next) return [[SectionId.CUSTOMERS, PermAction.DELETE]];
  const out: SectionCheck[] = [];
  const ext = diffEmbedded(prev.extinguishers, next.extinguishers);
  if (!sameValue(prev.sectionColors, next.sectionColors)) ext.edited = true;
  pushDiff(out, SectionId.EXTINGUISHERS, ext);
  pushDiff(out, SectionId.HOSES, diffEmbedded(prev.hoses, next.hoses));
  if (recordChanged(prev, next)) {
    out.push([SectionId.CUSTOMERS, PermAction.EDIT]);
  }
  return out;
}

/**
 * The rights a hose-options write needs: an option that appears is a create,
 * one that disappears a delete. Any other change (there is no rename in the
 * UI) counts as a create.
 */
export function hoseConfigWriteChecks(
  prev: HoseConfig,
  next: HoseConfig
): SectionCheck[] {
  const p = prev as unknown as Record<string, string[] | undefined>;
  const n = next as unknown as Record<string, string[] | undefined>;
  let created = false;
  let deleted = false;
  let other = false;
  for (const k of new Set([...Object.keys(p), ...Object.keys(n)])) {
    const before = p[k] ?? [];
    const after = n[k] ?? [];
    if (after.some(v => !before.includes(v))) created = true;
    if (before.some(v => !after.includes(v))) deleted = true;
    if (!sameValue(before, after)) other = true;
  }
  const out: SectionCheck[] = [];
  if (created || (other && !deleted)) {
    out.push([SectionId.HOSE_OPTIONS, PermAction.CREATE]);
  }
  if (deleted) out.push([SectionId.HOSE_OPTIONS, PermAction.DELETE]);
  return out;
}
